/* eslint-disable prefer-const */
import React, { useCallback, useState } from 'react'
import { useFormikContext } from 'formik'
import { Grid, Paper } from '@mui/material'

import { useLayoutContext } from '../../Context/LayoutContext'
import {
  IInitialValuesForm,
  IApiResponse,
  IItemTypes,
  IProcessMerchants,
  IItemTypesR,
} from '../../interfaces/payDetails'

import { metadata } from '../../config'
import { Buttons } from '../../config/global'
import { Button, I3OneUIProvider, AutoComplete, TextField } from '@i3one/react-ui'
import '@i3one/react-ui/style.css'

function throttle(cb: any, delay: number) {
  let wait = false
  let storedArgs: any = null

  function checkStoredArgs() {
    if (storedArgs == null) {
      wait = false
    } else {
      cb(...storedArgs)
      storedArgs = null
      setTimeout(checkStoredArgs, delay)
    }
  }

  return (...args: any[]) => {
    if (wait) {
      storedArgs = args
      return
    }

    cb(...args)
    wait = true
    setTimeout(checkStoredArgs, delay)
  }
}
const PayFields = ({ apiResponse }: { apiResponse: IApiResponse[] | [] }) => {
  const { setFieldValue, touched, values, errors, handleBlur, resetForm, setErrors } =
    useFormikContext<IInitialValuesForm>()
  const layout = useLayoutContext()
  const [allowToContinue, setAllowToContinue] = useState<boolean>(true)
  const [officeInputValue, setOfficeInputValue] = useState<string>(values.office?.title ?? '')
  const [itemTypeInputValue, setItemTypeInputValue] = useState<string>(
    values.itemType?.typeDesc ?? '',
  )

  const throttledSetField = useCallback(
    throttle((e: string, key: string) => {
      setFieldValue(`itemList[${key}]`, e)
    }, 500),
    [],
  )

  // const onChangeOffice = (value: string) => {
  const onChangeOffice = (value: IProcessMerchants) => {
    if (values.office === null || values.office!.id !== value.id) {
      resetForm({
        values: {
          ...values,
          office: value,
          itemType: null,
          id: value.id,
          itemList: {} as IItemTypesR,
          list: {} as IItemTypesR,
          amount: '',
        },
      })
    }
  }
  const onChangeItemTypes = (itemTypeValue: IItemTypes) => {
    if (values.itemType === null || values.itemType !== itemTypeValue) {
      resetForm({
        values: {
          ...values,
          itemType: itemTypeValue,
          itemList: {} as IItemTypesR,
          list: {} as IItemTypesR,
        },
      })

      let obj1: Record<string, string> = {}
      let obj2: Record<string, string> = {}
      for (let value of Array(itemTypeValue)) {
        Object.entries(value).map(([key, val]) => {
          if (key.substring(0, 4) === 'desc') {
            if (val) {
              obj1[key] = val
              obj2[key] = ''
            }
          }
        })
      }
      setFieldValue('list', obj1)
      setFieldValue('itemList', obj2)
    }
  }

  const handleNumericError = (error: string | any) => {
    // Handle errors from NumericTextField here
    setErrors({ ...errors, amount: error })
    if (error) {
      setAllowToContinue(false)
    } else {
      setAllowToContinue(true)
    }
  }

  const firstListKey = Object.keys(values.list)[0]
  const canContinue =
    !!values.office &&
    !!values.itemType &&
    (!firstListKey || !!(values.itemList as unknown as Record<string, string>)[firstListKey]) &&
    !!values.amount &&
    allowToContinue

  return (
    <div>
      <Paper
        elevation={10}
        sx={{ p: 3, mr: layout === 'desktop' ? 20 : 0, ml: layout === 'desktop' ? 20 : 0 }}
      >
        <Grid container columnSpacing={1} rowSpacing={{ xs: 2.5, sm: 2.5, md: 2, lg: 2 }}>
          <Grid item xs={12} md={12} lg={12}>
            <Grid item xs={12} md={12} lg={12}>
              <Grid container columnSpacing={2} rowSpacing={{ xs: 2, sm: 2, md: 1.5, lg: 1.5 }}>
                <Grid item xs={12} md={6} lg={6}>
                  <AutoComplete
                    role='combobox'
                    id={metadata.office.id}
                    options={apiResponse.map((item) => item.title)}
                    value={officeInputValue}
                    label={metadata.office.label}
                    size='md'
                    required
                    name={metadata.office.id}
                    state={touched.office && Boolean(errors.office) ? 'error' : 'default'}
                    helperText={touched.office && errors.office}
                    onBlur={handleBlur}
                    onChange={(e) => {
                      const inputVal = e.target.value as string
                      setOfficeInputValue(inputVal)
                      const selected = apiResponse.find((item) => item.title === inputVal)
                      if (selected) {
                        onChangeOffice(selected as unknown as IProcessMerchants)
                        setItemTypeInputValue('')
                      } else if (!inputVal) {
                        resetForm({
                          values: {
                            ...values,
                            office: null,
                            itemType: null,
                            id: 0,
                            itemList: {} as IItemTypesR,
                            list: {} as IItemTypesR,
                            amount: '',
                          },
                        })
                        setItemTypeInputValue('')
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={6}>
                  <AutoComplete
                    role='combobox'
                    id={metadata.itemType.id}
                    options={
                      values.office
                        ? apiResponse
                            .filter((item) => item.id === values.office?.id)[0]
                            .itemTypes.map((item1) => item1.typeDesc)
                        : []
                    }
                    value={itemTypeInputValue}
                    label={metadata.itemType.label}
                    size='md'
                    required
                    name={metadata.itemType.id}
                    state={
                      !values.office
                        ? 'disabled'
                        : touched.itemType && Boolean(errors.itemType)
                        ? 'error'
                        : 'default'
                    }
                    helperText={touched.itemType && errors.itemType}
                    onBlur={handleBlur}
                    onChange={(e) => {
                      const inputVal = e.target.value as string
                      setItemTypeInputValue(inputVal)
                      const itemTypes = values.office
                        ? apiResponse.filter((item) => item.id === values.office?.id)[0]?.itemTypes
                        : []
                      const selected = itemTypes?.find((i) => i.typeDesc === inputVal)
                      if (selected) {
                        onChangeItemTypes(selected)
                      } else if (!inputVal) {
                        resetForm({
                          values: {
                            ...values,
                            itemType: null,
                            itemList: {} as IItemTypesR,
                            list: {} as IItemTypesR,
                          },
                        })
                      }
                    }}
                  />
                  <div style={{ marginBottom: '1rem' }} />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={12} lg={12}>
              <Grid container columnSpacing={2} rowSpacing={{ xs: 2, sm: 2, md: 1.5, lg: 1.5 }}>
                <Grid item xs={12} md={12} lg={12}>
                  <React.Fragment>
                    {Object.entries(values.list).map(([key, val]: [string, string], index) => {
                      const capitalizedLabel = val.charAt(0).toUpperCase() + val.slice(1)
                      const itemListTouched = touched.itemList as Record<
                        string,
                        boolean | undefined
                      >
                      const itemListErrors = errors.itemList as Record<string, string | undefined>
                      return (
                        <>
                          <TextField
                            id={`${key}-input`}
                            key={val + index}
                            required={index === 0}
                            state={
                              itemListTouched?.[key] && Boolean(itemListErrors?.[key])
                                ? 'error'
                                : 'default'
                            }
                            variant='underline'
                            helperText={itemListTouched?.[key] && itemListErrors?.[key]}
                            name={`itemList[${key}]`}
                            label={capitalizedLabel}
                            onBlur={handleBlur}
                            onChange={(e) => {
                              throttledSetField(e.target.value, key)
                            }}
                          />
                          <div style={{ marginBottom: '1rem' }} />
                        </>
                      )
                    })}
                  </React.Fragment>

                  {!values.itemType ? null : (
                    <>
                      <TextField
                        id='amount-input'
                        key={'amount'}
                        required
                        state={touched.amount && Boolean(errors.amount) ? 'error' : 'default'}
                        variant='amount'
                        helperText={touched.amount && errors.amount}
                        name={metadata.amount.id}
                        label={metadata.amount.label}
                        onBlur={(e) => {
                          handleBlur(e)
                          const val = e.target.value
                          if (val && !isNaN(Number(val))) {
                            setFieldValue('amount', Number(val).toFixed(2))
                          }
                        }}
                        value={values.amount}
                        onChange={(e) => {
                          const val = e.target.value
                          if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) {
                            setFieldValue('amount', val)
                          }
                        }}
                      />
                      <div style={{ marginBottom: '1rem' }} />
                    </>
                  )}
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={12} lg={12}>
              <Grid container columnSpacing={2} rowSpacing={{ xs: 2, sm: 2, md: 1.5, lg: 1.5 }}>
                <Grid item sm={12} xs={12} md={12} lg={12}>
                  <Grid container justifyContent='right' marginTop={1} alignContent='center'>
                    <I3OneUIProvider>
                      <Button id='continue-button' size='md' disabled={!canContinue} type='submit'>
                        {Buttons.CONTINUE.toUpperCase()}
                      </Button>
                    </I3OneUIProvider>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </div>
  )
}

export default PayFields
